import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef, useEffect, useState } from "react";
import { Layout } from "@/components/site/Layout";
import { useServicesList } from "@/lib/services";
import { ArrowRight, Mail, Zap, ShieldCheck, Sparkles, Award, Layers, Cpu } from "lucide-react";
import { useT, useMedia } from "@/lib/i18n";
import heroBgImg from "@/assets/pulso33-hero.jpg";
import aiAgentsImg from "@/assets/service-ai-agents.jpg";
import webAppsImg from "@/assets/service-web-apps.jpg";
import aiIntegrationImg from "@/assets/service-ai-integration.jpg";
import biImg from "@/assets/service-bi.jpg";
import automationImg from "@/assets/service-automation.jpg";
import cloudImg from "@/assets/service-cloud-devops.jpg";

export const Route = createFileRoute("/")({
  component: Home,
  head: () => ({
    meta: [
      { title: "PULSO33 — Count On Us. End-to-end IT services." },
      {
        name: "description",
        content:
          "PULSO33 builds AI agents, web applications, infrastructure, automation and BI. Real engineers shipping production-grade systems.",
      },
      { property: "og:title", content: "PULSO33 — Count On Us" },
      {
        property: "og:description",
        content:
          "End-to-end IT services: AI agents, web applications, infrastructure, automation and BI. We build, deploy, manage and support.",
      },
      { property: "og:url", content: "https://pulso33.com/" },
    ],
    links: [{ rel: "canonical", href: "/" }],
  }),
});

function CountUp({ end, suffix = "", duration = 1800 }: { end: number; suffix?: string; duration?: number }) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const tick = (now: number) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(end * eased));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{value.toLocaleString()}{suffix}</span>;
}

const CLIENT_LOGOS = [
  "Fono Postres", "TATA Consultancy Services", "Betway", "Modula 4", "Bank of America",
  "NASA", "Disney", "Deutsche Bank", "Derivco", "RAKETECH", "Ibermática", "CA Technologies",
  "No Veas", "ABBA", "Corpus Dei", "3 o'CloCk",
];

const SERVICE_IMAGES: Record<string, string> = {
  "ai-agents": aiAgentsImg,
  "web-applications": webAppsImg,
  "ai-integration": aiIntegrationImg,
  "business-intelligence": biImg,
  "automation": automationImg,
  "cloud-devops": cloudImg,
};

function Home() {
  const { tk } = useT();
  const services = useServicesList();
  const heroBg = useMedia("home.hero.bg", heroBgImg, "PULSO33 — abstract tech");

  const featuredServices = services.filter((s) => SERVICE_IMAGES[s.slug]);

  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-gold/15">
        <img
          src={heroBg.url}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background/80 to-background/60" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 text-center">
          <Link
            to="/products/agente"
            className="group inline-flex items-center gap-3 mb-8 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-[10px] tracking-[0.3em] uppercase text-foreground hover:border-gold hover:bg-gold/20 transition-colors"
          >
            <span className="rounded-full bg-gold text-primary-foreground px-2 py-0.5 text-[9px] font-semibold tracking-[0.25em]">
              {tk("home.hero.badge.new", "New")}
            </span>
            <span className="text-foreground/80">
              {tk("home.hero.badge.text", "Meet AgentE — our 24/7 AI sales agent")}
            </span>
            <ArrowRight size={11} className="text-gold group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <div className="inline-flex items-center gap-3 mb-8 w-full justify-center">
            <span className="gold-rule" />
            <span className="text-xs tracking-[0.4em] uppercase text-gold">
              {tk("home.kicker", "End-to-end IT services")}
            </span>
            <span className="gold-rule" />
          </div>
          <h1 className="font-display text-6xl md:text-8xl leading-[1.0] text-foreground">
            {tk("home.hero.title", "Count On Us!")}
          </h1>
          <p className="mt-10 text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {tk("home.hero.lead.1", "Transforming the ")}
            <span className="highlight-yellow text-foreground font-medium">{tk("home.hero.lead.it", "IT")}</span>
            {tk("home.hero.lead.2", " areas that matter most to your business. Development, infrastructure, ")}
            <span className="highlight-yellow text-foreground font-medium">{tk("home.hero.lead.ai", "AI integration")}</span>
            {tk("home.hero.lead.3", " — whatever you need, we make it happen.")}
          </p>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl mx-auto">
            {tk("home.hero.sub", "We work with ambitious people who are ready to grow.")}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-3 rounded-full bg-gold text-primary-foreground font-semibold px-8 py-4 text-xs tracking-[0.3em] uppercase hover:bg-gold-soft transition-colors"
            >
              {tk("home.hero.cta.contact", "Contact us")}{" "}
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-3 rounded-full border border-gold/50 text-foreground px-8 py-4 text-xs tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors"
            >
              {tk("home.hero.cta.services", "Services")}
            </Link>
          </div>
        </div>
      </section>

      {/* CLIENTS MARQUEE */}
      <section className="border-b border-gold/15 bg-card/30 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="text-center text-[10px] tracking-[0.4em] uppercase text-muted-foreground mb-6">
            {tk("home.clients.kicker", "Our people collaborated with")}
          </div>
          <div className="relative overflow-hidden">
            <div className="flex gap-12 animate-[scroll_45s_linear_infinite] whitespace-nowrap" style={{ animationName: "scroll" }}>
              {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((name, i) => (
                <span key={`${name}-${i}`} className="font-display text-sm tracking-[0.25em] uppercase text-foreground/60 hover:text-gold transition-colors">
                  {name}
                </span>
              ))}
            </div>
            <style>{`@keyframes scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="border-b border-gold/15">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.4em] uppercase text-gold">
              {tk("home.what.kicker", "What we do")}
            </span>
            <h2 className="font-display text-5xl md:text-6xl mt-4">
              {tk("home.what.title", "What We Do")}
            </h2>
            <p className="mt-6 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {tk("home.what.subtitle", "End-to-end IT services — from software development and AI to infrastructure, security, and support.")}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredServices.map((s) => (
              <Link
                key={s.slug}
                to="/services"
                className="group rounded-2xl border border-gold/15 bg-card/40 overflow-hidden hover:border-gold/60 hover:bg-card/60 transition-colors flex flex-col"
              >
                <div className="aspect-[4/3] overflow-hidden bg-card">
                  <img
                    src={SERVICE_IMAGES[s.slug]}
                    alt={s.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display text-2xl group-hover:text-gold transition-colors">{s.name}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{s.desc}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-gold">
                    {tk("home.what.cta", "Learn more")} <ArrowRight size={12} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/services/areas"
              className="text-xs tracking-[0.3em] uppercase text-gold border-b border-gold/40 pb-1 hover:border-gold inline-flex items-center gap-2"
            >
              {tk("home.what.all", "See all services")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* AGENTE PRODUCT PROMO */}
      <section className="relative border-b border-gold/15 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gold/10 via-background to-background" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs tracking-[0.4em] uppercase text-gold">
              {tk("home.agente.kicker", "Our first product · AgentE")}
            </span>
            <h2 className="font-display text-4xl md:text-6xl mt-5 leading-[1.05]">
              {tk("home.agente.title.a", "Reimagining how businesses ")}
              <span className="gold-gradient">{tk("home.agente.title.b", "sell on the internet")}</span>.
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-xl">
              {tk("home.agente.text", "AgentE is PULSO33's first SaaS: a 24/7 AI sales agent, multilingual website, CRM, bookings, Stripe payments and a professional marketplace — one platform, live in days.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/products/agente"
                className="group inline-flex items-center gap-3 rounded-full bg-gold text-primary-foreground font-semibold px-7 py-3 text-xs tracking-[0.3em] uppercase hover:bg-gold-soft transition-colors"
              >
                {tk("home.agente.cta", "Discover AgentE")}{" "}
                <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="https://agente.pulso33.com/"
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-7 py-3 text-xs tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors"
              >
                agente.pulso33.com
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { v: "24/7", l: tk("home.agente.s1", "AI agent selling") },
              { v: "5d", l: tk("home.agente.s2", "Go-live") },
              { v: "1", l: tk("home.agente.s3", "Platform, all in one") },
              { v: "∞", l: tk("home.agente.s4", "Languages & brands") },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl border border-gold/25 bg-card/40 p-6">
                <div className="font-display text-4xl gold-gradient">{s.v}</div>
                <div className="mt-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* STATS */}
      <section className="border-b border-gold/15 bg-card/30">
        <div className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
          {[
            { n: 15, suffix: "+", l: tk("home.stat.years", "Years building production systems") },
            { n: 440, suffix: "K+", l: tk("home.stat.users", "Users on platforms we've built") },
            { n: 50, suffix: "+", l: tk("home.stat.tech", "Technologies in our stack") },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-display text-6xl md:text-7xl gold-gradient">
                <CountUp end={s.n} suffix={s.suffix} />
              </div>
              <div className="mt-4 text-[11px] tracking-[0.3em] uppercase text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WE'RE UNIQUE */}
      <section className="border-b border-gold/15">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.4em] uppercase text-gold">
              {tk("home.unique.kicker", "We're unique")}
            </span>
            <h2 className="font-display text-5xl md:text-6xl mt-4">
              {tk("home.unique.title", "What makes us different")}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { i: Cpu, t: tk("home.unique.real.t", "Real Engineers"), d: tk("home.unique.real.d", "Real engineers, not resellers. We build, deploy, manage, and support — end to end."), stat: "100%" },
              { i: ShieldCheck, t: tk("home.unique.prod.t", "Production-First"), d: tk("home.unique.prod.d", "Monitoring, logging, error handling from day one. Zero downtime deployments."), stat: "0 downtime" },
              { i: Layers, t: tk("home.unique.full.t", "Full-Spectrum IT"), d: tk("home.unique.full.d", "Software, infrastructure, security, data, AI, CRM/ERP, help desk — one partner for everything."), stat: "360°" },
              { i: Zap, t: tk("home.unique.speed.t", "Speed Without Shortcuts"), d: tk("home.unique.speed.d", "Enterprise-grade solutions in days, not months. No corners cut."), stat: "30x" },
              { i: Award, t: tk("home.unique.own.t", "Full Ownership"), d: tk("home.unique.own.d", "You own the code, the infrastructure, the data. No lock-in, ever.") },
              { i: Sparkles, t: tk("home.unique.lang.t", "Bilingual Team"), d: tk("home.unique.lang.d", "Native English and Spanish support. Seamless communication across borders.") },
            ].map(({ i: Icon, t, d, stat }) => (
              <div key={t} className="rounded-2xl border border-gold/20 bg-card/30 p-7 hover:border-gold/60 transition-colors flex flex-col">
                <Icon className="text-gold" size={28} strokeWidth={1.4} />
                <h3 className="mt-5 font-display text-2xl">{t}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{d}</p>
                {stat && (
                  <div className="mt-5 font-display text-3xl gold-gradient">{stat}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${heroBg.url})` }} aria-hidden />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/60" aria-hidden />
        <div className="relative max-w-4xl mx-auto px-6 py-24 text-center">
          <span className="text-xs tracking-[0.4em] uppercase text-gold">
            {tk("home.cta.kicker", "Tell us about your project")}
          </span>
          <h2 className="font-display text-5xl md:text-7xl mt-5">
            {tk("home.cta.title", "Count On Us!")}
          </h2>
          <p className="mt-6 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {tk("home.cta.text", "Whatever IT challenge you're facing, we make it happen. Let's start the conversation.")}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="rounded-full bg-gold text-primary-foreground px-9 py-4 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-gold-soft transition-colors"
            >
              {tk("home.cta.contact", "Contact us")}
            </Link>
            <a
              href="mailto:info@pulso33.com"
              className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-9 py-4 text-xs tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors"
            >
              <Mail size={14} /> info@pulso33.com
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
